import type { ReactNode } from 'react'
import { Link } from 'react-router'

interface IconButtonProps {
  /** 아이콘만 서는 자리라 이름을 여기서 준다. 화면에 글자로 나타나지 않는다. */
  label: string
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  /** 다른 주소로 가는 자리면 링크로 세운다. 새 탭과 뒤로가기가 그대로 통한다. */
  to?: string
}

/**
 * 라벨 없이 아이콘만 두는 버튼.
 *
 * 이런 자리는 셋뿐이다. 화면 머리의 돌아가는 자리, 홈 헤더의 설정, 덮는 층과 안내 블록의
 * 닫기다. 그 밖의 자리에는 라벨이 있는 버튼을 쓴다.
 *
 * 면도 테두리도 두지 않고 색은 ink다. ink-muted를 쓰지 않는다. 흐린 획은 장식으로 읽혀 누를
 * 수 있다는 것을 알리지 못한다.
 *
 * 타깃은 44×44px이고 아이콘 둘레의 여백이 그 크기를 만든다. 누른 자리를 알리는 면은 그 타깃을
 * 채우는 원이다. 획에만 얹으면 눈에 보이지 않는다.
 */
export function IconButton({ label, children, onClick, disabled, to }: IconButtonProps) {
  const shape =
    'pressable text-ink flex size-11 items-center justify-center rounded-full disabled:opacity-60'

  if (to) {
    return (
      <Link to={to} aria-label={label} title={label} className={shape}>
        {children}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={shape}
    >
      {children}
    </button>
  )
}
